import React, { useState, useEffect } from 'react';
import axios from 'axios';

const AgentChecksPage = () => {
  const [checks, setChecks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all, pending, cleared, bounced
  const [companiesCount, setCompaniesCount] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortOrder, setSortOrder] = useState('asc');

  useEffect(() => {
    fetchChecks();
  }, []);

  const fetchChecks = async () => {
    try {
      const response = await axios.get('/api/field-agent/checks');
      setChecks(response.data.checks || []);
      setCompaniesCount(response.data.companies_count || 0);
    } catch (error) {
      console.error('Error fetching checks:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusColors = {
      pending: 'bg-yellow-100 text-yellow-700',
      cleared: 'bg-green-100 text-green-700',
      bounced: 'bg-red-100 text-red-700',
      cancelled: 'bg-gray-100 text-gray-700'
    };

    const statusLabels = {
      pending: 'ממתין',
      cleared: 'פרוע',
      bounced: 'חזר',
      cancelled: 'מבוטל'
    };

    return (
      <span className={`px-2 py-1 text-xs rounded-full ${statusColors[status]}`}>
        {statusLabels[status]}
      </span>
    );
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('he-IL', {
      style: 'currency',
      currency: 'ILS'
    }).format(amount);
  };

  const filteredChecks = checks
    .filter(check => {
      if (filter !== 'all' && check.status !== filter) return false;
      if (searchQuery.trim()) {
        const q = searchQuery.trim().toLowerCase();
        const matchesCompany = (check.company_name || '').toLowerCase().includes(q);
        const matchesCheckNum = (check.check_number || '').toLowerCase().includes(q);
        const matchesReceipt = (check.receipt_number || '').toLowerCase().includes(q);
        if (!matchesCompany && !matchesCheckNum && !matchesReceipt) return false;
      }
      return true;
    })
    .sort((a, b) => {
      const dateA = new Date(a.check_date).getTime();
      const dateB = new Date(b.check_date).getTime();
      return sortOrder === 'asc' ? dateA - dateB : dateB - dateA;
    });

  const statusCounts = {
    all: checks.length,
    pending: checks.filter(c => c.status === 'pending').length,
    cleared: checks.filter(c => c.status === 'cleared').length,
    bounced: checks.filter(c => c.status === 'bounced').length,
  };

  const totalAmount = filteredChecks.reduce((sum, check) => sum + parseFloat(check.amount), 0);

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" dir="rtl">
      {/* Page Header */}
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900">צ״קים שלי</h1>
        <p className="mt-2 text-sm text-gray-600">
          צפייה בכל הצ״קים של {companiesCount} החברות שלך
        </p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
        <div className="bg-white rounded-lg shadow p-4">
          <div className="text-sm text-gray-600 mb-1">סה"כ צ״קים</div>
          <div className="text-2xl font-bold text-gray-900">{statusCounts.all}</div>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <div className="text-sm text-gray-600 mb-1">ממתינים</div>
          <div className="text-2xl font-bold text-yellow-600">{statusCounts.pending}</div>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <div className="text-sm text-gray-600 mb-1">פרועים</div>
          <div className="text-2xl font-bold text-green-600">{statusCounts.cleared}</div>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <div className="text-sm text-gray-600 mb-1">חזרו</div>
          <div className="text-2xl font-bold text-red-600">{statusCounts.bounced}</div>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <div className="text-sm text-gray-600 mb-1">סה"כ סכום</div>
          <div className="text-2xl font-bold text-primary-600">{formatCurrency(totalAmount)}</div>
        </div>
      </div>

      {/* Search + Filters */}
      <div className="bg-white rounded-lg shadow p-4 mb-4 space-y-3">
        {/* Search */}
        <div className="flex gap-3 items-center">
          <div className="flex-1 relative">
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="חיפוש לפי חברה, מספר צ'ק או מספר קבלה..."
              className="w-full border border-gray-300 rounded-md px-4 py-2 pr-10 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                &times;
              </button>
            )}
          </div>
          <button
            onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
            className="px-4 py-2 rounded-md bg-gray-100 hover:bg-gray-200 text-sm text-gray-700 whitespace-nowrap"
          >
            {sortOrder === 'asc' ? 'תאריך: קרוב ← רחוק' : 'תאריך: רחוק ← קרוב'}
          </button>
        </div>
        {/* Status Filters */}
        <div className="flex flex-wrap gap-3">
          <button
            onClick={() => setFilter('all')}
            className={`px-4 py-2 rounded-md transition-colors ${
              filter === 'all' 
                ? 'bg-primary-600 text-white' 
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            הכל ({statusCounts.all})
          </button>
          <button
            onClick={() => setFilter('pending')}
            className={`px-4 py-2 rounded-md transition-colors ${
              filter === 'pending' 
                ? 'bg-yellow-600 text-white' 
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            ממתינים ({statusCounts.pending})
          </button>
          <button
            onClick={() => setFilter('cleared')}
            className={`px-4 py-2 rounded-md transition-colors ${
              filter === 'cleared' 
                ? 'bg-green-600 text-white' 
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            פרועים ({statusCounts.cleared})
          </button>
          <button
            onClick={() => setFilter('bounced')}
            className={`px-4 py-2 rounded-md transition-colors ${
              filter === 'bounced' 
                ? 'bg-red-600 text-white' 
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            חזרו ({statusCounts.bounced})
          </button>
        </div>
      </div>

      {/* Checks Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                  חברה
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                  מספר צ״ק
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                  בנק/סניף
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                  תאריך
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                  סכום
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                  סטטוס
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                  הערות
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredChecks.length === 0 ? (
                <tr>
                  <td colSpan="7" className="text-center py-12">
                    <svg
                      className="mx-auto h-12 w-12 text-gray-400"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                      />
                    </svg>
                    <h3 className="mt-2 text-sm font-medium text-gray-900">אין צ״קים</h3>
                    <p className="mt-1 text-sm text-gray-500">
                      {filter === 'all' 
                        ? 'טרם נרשמו צ״קים לחברות שלך' 
                        : 'אין צ״קים במצב זה'}
                    </p>
                  </td>
                </tr>
              ) : (
                filteredChecks.map((check) => (
                  <tr key={check.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{check.company_name}</div>
                      <div className="text-xs text-gray-500">
                        קבלה: {check.receipt_number}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {check.check_number}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                      {check.bank_number} / {check.branch_number}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {new Date(check.check_date).toLocaleDateString('he-IL', {
                        day: '2-digit',
                        month: '2-digit',
                        year: 'numeric'
                      })}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-green-600">
                      {formatCurrency(check.amount)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getStatusBadge(check.status)}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      <div className="max-w-xs truncate" title={check.notes || '-'}>
                        {check.notes || '-'}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
            {filteredChecks.length > 0 && (
              <tfoot className="bg-gray-50">
                <tr>
                  <td colSpan="4" className="px-6 py-3 text-sm font-bold text-gray-700">
                    סה"כ ({filteredChecks.length} צ״קים)
                  </td>
                  <td className="px-6 py-3 text-sm font-bold text-green-600 whitespace-nowrap">
                    {formatCurrency(totalAmount)}
                  </td>
                  <td colSpan="2"></td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>

      {/* Info Note */}
      {checks.length > 0 && (
        <div className="mt-4 bg-blue-50 border-r-4 border-blue-400 p-4 rounded">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="mr-3">
              <p className="text-sm text-blue-700">
                מוצגים צ״קים מ-{companiesCount} חברות שאתה מנהל. 
                רק האדמין יכול לעדכן סטטוס צ״קים.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AgentChecksPage;

